import { type Dispatch, type FC, type SetStateAction } from "react";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import { MoonLoader } from "react-spinners";
import { obtenerClaseEstadoPagoCliente } from "../../../utils/clienteEstadoPago";
import { AbonoModal } from "../../modals/AbonoModal/AbonoModal";
import type { AbonoCliente, UsuarioRuta as AbonoUsuarioRuta } from "../../modals/AbonoModal/types";
import { DetallesClienteModal } from "../../modals/DetallesClienteModal/DetallesClienteModal";
import { EditarClienteModal } from "../../modals/EditarClienteModal/EditarClienteModal";
import type { Cliente as EditarClienteData } from "../../modals/EditarClienteModal/types";
import { ModalDeleteCliente } from "../../modals/ModalDeleteCliente/ModalDeleteCliente";
import type { AnyObject as DeleteClienteAnyObject } from "../../modals/ModalDeleteCliente/types";
import { ModalPreguntaRenovacion } from "../../modals/ModalPreguntaRenovacion/ModalPreguntaRenovacion";
import { useTablaClientes } from "./tablaClientesHooks";
import type { TablaClientesProps } from "./types";

const EMPTY_STATE_COLSPAN = 8;

export const TablaClientes: FC<TablaClientesProps> = (props) => {
  const {
    closeMoveBlockedModal,
    filteredClients,
    formatearNumeros,
    handleAbono,
    handleButtonClick,
    handleChangeOrder,
    handleDeleteClick,
    handleDetallesCliente,
    handleEditClick,
    handleMoveCliente,
    handleSelectedAbonoUpdate,
    inputValues,
    isAbono,
    isModalDelete,
    isModalEdit,
    isModalPreguntaRenovacion,
    isMovingClient,
    moveBlockedModal,
    rutasFiltradas,
    selectedAbono,
    selectedCliente,
    selectedDetallesCliente,
    setClientes,
    setIsAbono,
    setIsModalDelete,
    setIsModalEdit,
    setIsModalPreguntaRenovacion,
    setSelectedCliente,
    setVerDetallesCliente,
    showAdminActions,
    showMoveColumn,
    userData,
    verDetallesCliente,
  } = useTablaClientes(props);

  return (
    <>
      {isModalDelete ? (
        <ModalDeleteCliente
          setIsModalDelete={setIsModalDelete}
          selectedCliente={selectedCliente}
          userData={userData}
          usuarioRuta={props.usuarioRuta}
          setClientes={setClientes as Dispatch<SetStateAction<DeleteClienteAnyObject[]>>}
        />
      ) : null}

      {isModalEdit ? (
        <EditarClienteModal
          setIsModalEdit={setIsModalEdit}
          selectedCliente={selectedCliente as EditarClienteData | null}
          setSelectedCliente={
            setSelectedCliente as Dispatch<SetStateAction<EditarClienteData | null>>
          }
          usuarioRuta={props.usuarioRuta as DeleteClienteAnyObject}
          setClientes={setClientes as Dispatch<SetStateAction<EditarClienteData[]>>}
        />
      ) : null}

      {isAbono ? (
        <AbonoModal
          setIsModalPreguntaRenovacion={setIsModalPreguntaRenovacion}
          setIsAbono={setIsAbono}
          selectedAbono={selectedAbono}
          usuarioRuta={props.usuarioRuta as AbonoUsuarioRuta}
          setUsuarioRuta={props.setUsuarioRuta as Dispatch<SetStateAction<AbonoUsuarioRuta | null>>}
          setSelectedAbono={(updatedAbono) => {
            handleSelectedAbonoUpdate(updatedAbono as AbonoCliente);
          }}
        />
      ) : null}

      {verDetallesCliente ? (
        <DetallesClienteModal
          setVerDetallesCliente={setVerDetallesCliente}
          selectedDetallesCliente={selectedDetallesCliente}
        />
      ) : null}

      {moveBlockedModal ? (
        <div className="w-full min-h-screen h-screen z-40 bg-black bg-opacity-50 px-2 md:px-8 flex justify-center items-center fixed top-0 left-0">
          <div className="bg-gray-200 w-full max-w-[420px] p-4 rounded-md shadow-lg">
            <h2 className="text-center text-lg font-semibold uppercase">Traspaso bloqueado</h2>
            <p className="text-center mt-3 font-medium">
              {`No se puede pasar el cliente "${moveBlockedModal.clienteNombre}" para la ruta "${moveBlockedModal.rutaNombre}" porque ya ese cliente consta con movimientos.`}
            </p>
            <div className="flex justify-center mt-4">
              <button
                type="button"
                className="bg-[#8131bd] text-white px-4 py-1 rounded-md min-w-[120px]"
                onClick={closeMoveBlockedModal}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isMovingClient ? (
        <div className="w-full flex-col top-0 left-0 min-h-screen h-screen z-30 bg-black bg-opacity-50 px-2 md:px-8 flex justify-center items-center fixed">
          <MoonLoader size={48} color="#ffffff" />
          <p className="text-white font-semibold text-base">Moviendo Cliente...</p>
        </div>
      ) : null}

      {isModalPreguntaRenovacion ? (
        <ModalPreguntaRenovacion
          setIsModalPreguntaRenovacion={setIsModalPreguntaRenovacion}
          setIsModalCreateClienteExistente={props.setIsModalCreateClienteExistente}
        />
      ) : null}

      <table className="min-w-full mt-2 bg-white">
        <thead>
          <tr className="bg-[#8131bd] uppercase text-sm text-white">
            {props.isEditIndex ? (
              <th className="border border-black w-6 px-2 py-1 whitespace-nowrap">Orden</th>
            ) : null}
            {props.isEditIndex ? (
              <th className="border border-black w-6 px-2 py-1 whitespace-nowrap">cambiar</th>
            ) : null}
            <th className="border border-black w-6 px-2 py-1 whitespace-nowrap">#</th>
            <th className="border border-black min-w-fit px-2 py-1 whitespace-nowrap">Cliente</th>
            <th className="border border-black min-w-fit px-2 py-1 whitespace-nowrap">abono hoy</th>
            <th className="border border-black w-fit px-2 py-1 whitespace-nowrap">cuota</th>
            <th className="border border-black w-fit px-2 py-1 whitespace-nowrap">c.pag</th>
            <th className="border border-black w-fit px-2 py-1 whitespace-nowrap">c.res</th>
            {showAdminActions ? (
              <>
                <th className="border border-black w-10 px-2 py-1 whitespace-nowrap">Editar</th>
                <th className="border border-black w-10 px-2 py-1 whitespace-nowrap">Eliminar</th>
              </>
            ) : null}

            <th className="border border-black w-fit px-2 py-1 whitespace-nowrap">detalles</th>
            {showMoveColumn ? (
              <th className="border border-black w-[200px] md:w-fit px-10 md:px-2 py-1 whitespace-nowrap">
                mover
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {filteredClients.length > 0 ? (
            filteredClients.map((item, index) => (
              <tr
                key={item.uid}
                className={`${
                  index % 2 === 0 ? "bg-gray-300" : "bg-gray-100"
                } border border-black text-sm font-semibold py-2`}
              >
                {props.isEditIndex ? (
                  <td className="items-center uppercase border-black flex justify-center px-1 py-2 whitespace-nowrap">
                    <input
                      type="number"
                      placeholder={item.posicion?.toString() ?? ""}
                      className="w-full text-center border border-black focus:outline-none"
                      value={inputValues[item.uid] || ""}
                      onChange={(event) =>
                        handleChangeOrder(item.uid, parseInt(event.target.value, 10) - 1)
                      }
                    />
                  </td>
                ) : null}

                {props.isEditIndex ? (
                  <td className="px-2 py-2 border border-black whitespace-nowrap">
                    <button
                      onClick={handleButtonClick}
                      className="border w-full border-black bg-green-500 rounded-md"
                    >
                      OK
                    </button>
                  </td>
                ) : null}

                <td
                  className={`whitespace-nowrap border border-black text-center px-2 py-2 ${obtenerClaseEstadoPagoCliente(item)}`}
                >
                  {index + 1}
                </td>

                <td className="whitespace-nowrap items-center uppercase border-black min-w-max flex justify-center px-1 py-2">
                  {item.nombreCliente}
                </td>

                <td
                  className={`${item.abono >= item.pagoDiario ? "bg-green-300" : ""} ${
                    item.abono < item.pagoDiario && item.abono > 0 ? "bg-yellow-200" : ""
                  } whitespace-nowrap border border-black px-2 w-fit text-center py-2`}
                  onClick={() => handleAbono(item)}
                >
                  R${formatearNumeros(item.abono)}
                </td>

                <td className="whitespace-nowrap border border-black px-2 w-fit text-center py-2">
                  {`R$${formatearNumeros(item.pagoDiario)}`}
                </td>

                <td className="whitespace-nowrap border border-black px-2 w-fit text-center py-2">
                  {Math.floor(item.totalAbono / item.pagoDiario)}
                </td>

                <td className="whitespace-nowrap border border-black px-2 w-fit text-center py-2">
                  {item.cuotasPactadas - Math.floor(item.totalAbono / item.pagoDiario)}
                </td>

                {showAdminActions ? (
                  <>
                    <td className="whitespace-nowrap border border-black w-10 text-center px-2 py-1 font-semibold">
                      <AiOutlineEdit
                        onClick={() => handleEditClick(item)}
                        className="cursor-pointer my-0 mx-auto"
                      />
                    </td>

                    <td className="whitespace-nowrap border border-black w-10 px-2 py-1 font-semibold">
                      <AiOutlineDelete
                        className="cursor-pointer my-0 mx-auto"
                        onClick={() => handleDeleteClick(item)}
                      />
                    </td>
                  </>
                ) : null}

                <td
                  onClick={() => handleDetallesCliente(item)}
                  className="border whitespace-nowrap border-black px-2 w-fit text-center py-2 underline uppercase cursor-pointer"
                >
                  ver
                </td>

                {showMoveColumn ? (
                  <td className="border whitespace-nowrap border-black px-2 w-fit text-center py-2 underline uppercase cursor-pointer">
                    <select
                      className="flex-1 rounded-r-md w-full px-1 focus:border-transparent focus:outline-none"
                      defaultValue="Seleccionar"
                      onChange={(event) => {
                        const nuevaRutaUid = event.target.value;
                        const selectElement = event.currentTarget;

                        if (nuevaRutaUid !== "Seleccionar") {
                          void handleMoveCliente(item, nuevaRutaUid).finally(() => {
                            selectElement.value = "Seleccionar";
                          });
                        }
                      }}
                    >
                      <option value="Seleccionar" disabled>
                        Seleccionar
                      </option>
                      {rutasFiltradas.map((ruta) => (
                        <option value={ruta.uid} key={ruta.uid}>
                          {ruta.nombreRuta}
                        </option>
                      ))}
                    </select>
                  </td>
                ) : null}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={EMPTY_STATE_COLSPAN}>No hay datos disponibles.</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};
